(() => {
if (window.RaidageProductPage) { window.RaidageProductPage.init(); return; }
/**
 * Product Page JavaScript
 * Handles gallery, size selection, tabs, and size calculator
 */

class ProductGallery {
  constructor(root, signal) {
    this.root = root;
    this.signal = signal;
    this.slider = this.root.querySelector('[data-main-slider]');
    this.slides = this.root.querySelectorAll('.product-gallery__slide');
    this.prevBtn = this.root.querySelector('[data-slider-prev]');
    this.nextBtn = this.root.querySelector('[data-slider-next]');
    this.currentSlideEl = this.root.querySelector('[data-current-slide]');
    this.thumbs = this.root.querySelectorAll('[data-thumb-index]');
    this.currentSlide = 0;

    this.init();
  }

  init() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigateSlide('prev'), { signal: this.signal });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigateSlide('next'), { signal: this.signal });
    }
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => this.showSlide(parseInt(thumb.dataset.thumbIndex)), { signal: this.signal });
    });

    if (this.slider) {
      this.slider.addEventListener('scroll', () => this.handleScroll(), { passive: true, signal: this.signal });
    }
    this.signal.addEventListener('abort', () => clearTimeout(this._scrollTimer), { once: true });
  }

  navigateSlide(direction) {
    if (direction === 'prev') {
      this.showSlide(this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1);
    } else {
      this.showSlide(this.currentSlide === this.slides.length - 1 ? 0 : this.currentSlide + 1);
    }
  }

  showSlide(index) {
    this.slides.forEach(s => s.classList.remove('active'));
    this.thumbs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-pressed', 'false'); });

    if (this.slides[index]) {
      this.slides[index].classList.add('active');
      if (this.thumbs[index]) {
        this.thumbs[index].classList.add('active'); this.thumbs[index].setAttribute('aria-pressed', 'true');
      }
      if (this.currentSlideEl) {
        this.currentSlideEl.textContent = index + 1;
      }
      this.currentSlide = index;

      if (this.slider) {
        this.slider.scrollTo({
          left: this.slides[index].offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }
  }

  handleScroll() {
    if (!this.slider || !this.slides.length) return;
    clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(() => {
      const slideWidth = this.slider.clientWidth;
      if (!slideWidth) return;
      const index = Math.round(this.slider.scrollLeft / slideWidth);
      if (index === this.currentSlide || !this.slides[index]) return;
      this.slides.forEach(s => s.classList.remove('active'));
      this.thumbs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-pressed', 'false'); });
      this.slides[index].classList.add('active');
      if (this.thumbs[index]) {
        this.thumbs[index].classList.add('active');
        this.thumbs[index].setAttribute('aria-pressed', 'true');
      }
      if (this.currentSlideEl) this.currentSlideEl.textContent = index + 1;
      this.currentSlide = index;
    }, 80);
  }
}

class ProductSizeSelector {
  constructor(root, signal, gallery) {
    this.root = root;
    this.gallery = gallery;
    this.sizeButtons = [...root.querySelectorAll('.product-form__size[data-size]')];
    this.variantInput = root.querySelector('[data-variant-input]');
    this.selectedSizeEl = root.querySelector('[data-selected-size]');
    this.addToCartBtn = root.querySelector('[data-add-to-cart]');
    this.buttonText = root.querySelector('[data-button-text]');
    this.page = root.querySelector('[data-product-page]');
    try { this.data = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '{}'); }
    catch (_) { this.data = {}; }
    this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
    this.selectedVariant = this.variants.find(v => String(v.id) === String(this.data.initialVariantId)) || null;
    this.baseOptions = this.selectedVariant ? [...this.selectedVariant.options] : [...(this.data.fixedOptions || [])];
    this.pending = false;
    this.sizeButtons.forEach(btn => btn.addEventListener('click', () => this.selectSize(btn), { signal }));
    this.sync(this.selectedVariant, false);
  }

  resolveOptions(options) {
    if (!options.length || options.some(v => v === null || v === undefined)) return null;
    const matches = this.variants.filter(v => Array.isArray(v.options) && v.options.length === options.length && v.options.every((value, i) => String(value) === String(options[i])));
    return matches.length === 1 ? matches[0] : null;
  }

  variantForButton(btn) {
    const options = [...this.baseOptions];
    options[this.data.optionIndex] = btn.dataset.size;
    return this.resolveOptions(options);
  }

  selectSize(btn) {
    if (btn.disabled || this.pending) return;
    const variant = this.variantForButton(btn);
    if (!variant || !variant.available) return;
    this.sync(variant, true);
  }

  canSubmit() {
    const v = this.selectedVariant;
    return !!(v && v.available && this.page?.dataset.inStoreOnly !== 'true' &&
      String(this.variantInput?.value) === String(v.id) && this.resolveOptions(v.options)?.id === v.id);
  }

  sync(variant, announce = true) {
    this.selectedVariant = variant;
    if (variant) this.baseOptions = [...variant.options];
    this.sizeButtons.forEach(btn => {
      const candidate = this.variantForButton(btn);
      const selected = !!(variant && candidate && String(candidate.id) === String(variant.id));
      btn.dataset.variantId = candidate?.id || '';
      btn.disabled = this.pending || !candidate?.available;
      btn.classList.toggle('sold-out', !candidate?.available);
      btn.classList.toggle('selected', selected);
      btn.setAttribute('aria-pressed', String(selected));
    });
    if (this.variantInput) this.variantInput.value = variant?.id || '';
    if (this.selectedSizeEl) this.selectedSizeEl.textContent = variant ? variant.options[this.data.optionIndex] : `Select ${(this.data.optionName || 'size').toLowerCase()}`;
    this.updateButton();
    if (!variant) return;
    const price = this.root.querySelector('[data-product-price]');
    const template = this.root.querySelector(`template[data-variant-price="${variant.id}"]`);
    if (price && template) price.replaceChildren(template.content.cloneNode(true));
    const points = this.root.querySelector('[data-product-points]');
    if (points) points.textContent = `EARN ${variant.points} RAIDAGE POINTS`;
    const stock = this.root.querySelector('[data-product-stock]');
    if (stock) { stock.textContent = variant.available ? 'IN STOCK' : 'SOLD OUT'; stock.classList.toggle('sold-out', !variant.available); }
    if (variant.imageId) {
      const index = [...this.root.querySelectorAll('[data-image-id]')].findIndex(el => String(el.dataset.imageId) === String(variant.imageId));
      if (index >= 0 && index !== this.gallery.currentSlide) this.gallery.showSlide(index);
    }
    if (announce) {
      const status = this.root.querySelector('[data-product-status]');
      if (status) status.textContent = `${variant.options.join(' / ')}: ${variant.available ? 'In stock' : 'Sold out'}`;
    }
  }

  updateButton() {
    if (this.addToCartBtn) this.addToCartBtn.disabled = this.pending || !this.canSubmit();
    if (this.buttonText) this.buttonText.textContent = !this.selectedVariant ? `SELECT A ${(this.data.optionName || 'size').toUpperCase()}` : this.selectedVariant.available ? 'ADD TO CART' : 'SOLD OUT';
  }
}

class ProductInfoTabs {
  constructor(root, signal) {
    this.root = root;
    this.signal = signal;
    this.triggers = this.root.querySelectorAll('[data-info-tab]');
    this.contents = this.root.querySelectorAll('[data-tab-content]');
    this.closeButtons = this.root.querySelectorAll('[data-close-tab]');
    this.items = this.root.querySelectorAll('.product-info-tabs__item');

    this.init();
  }

  init() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', () => this.toggleTab(trigger), { signal: this.signal });
    });

    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.resetTabs(), { signal: this.signal });
    });
  }

  toggleTab(trigger) {
    const parentItem = trigger.closest('.product-info-tabs__item');
    if (!parentItem) return;

    const targetContent = parentItem.querySelector(`[data-tab-content="${trigger.dataset.infoTab}"]`);
    if (!targetContent) return;

    const wasOpen = parentItem.classList.contains('is-open');
    const isDesktop = window.innerWidth >= 990;
    const productInfo = parentItem.closest('.product-info');

    this.resetTabs();

    if (wasOpen) return;

    parentItem.classList.add('is-open');
    trigger.classList.add('active');
    targetContent.classList.add('is-open');
    
    if (isDesktop && productInfo) {
      productInfo.classList.add('product-info--tab-open');
    }
    
    // Don't auto-scroll on mobile - let user decide
  }

  resetTabs() {
    this.items.forEach(item => item.classList.remove('is-open'));
    this.triggers.forEach(trigger => trigger.classList.remove('active'));
    this.contents.forEach(content => {
      content.classList.remove('is-open');
      content.style.display = '';
    });
    this.root.querySelectorAll('.product-info--tab-open').forEach(el => {
      el.classList.remove('product-info--tab-open');
    });
  }
}

/**
 * SizeGuide — renders the factory size chart from
 * product.metafields.custom.size_chart_data and provides an optional
 * single-measurement picker. Everything (sizes, labels, measurement points,
 * units, product type) comes from the metafield JSON; nothing is hardcoded.
 * See SIZING.md for the schema.
 */
class SizeCalculator {
  constructor(root, signal) {
    this.root = root;
    this.signal = signal;
    this.trigger = this.root.querySelector('[data-size-calc-trigger]');
    this.modal = this.root.querySelector('[data-size-calculator]');
    this.closeButtons = this.modal ? this.modal.querySelectorAll('[data-modal-close]') : [];
    this.dataEl = this.root.querySelector('[data-size-chart]');
    this.tableWrap = this.root.querySelector('[data-size-chart-table]');
    this.emptyEl = this.root.querySelector('[data-size-chart-empty]');
    this.titleEl = this.root.querySelector('[data-size-guide-title]');
    this.subtitleEl = this.root.querySelector('[data-size-guide-subtitle]');
    this.pickerForm = this.root.querySelector('[data-size-picker-form]');
    this.pickerLabel = this.root.querySelector('[data-picker-label]');
    this.pickerInput = this.root.querySelector('[data-picker-input]');
    this.pickerUnit = this.root.querySelector('[data-picker-unit]');
    this.result = this.root.querySelector('[data-size-result]');
    this.recommendedSizeEl = this.root.querySelector('[data-recommended-size]');
    this.resultNoteEl = this.root.querySelector('[data-result-note]');
    this.selectSizeBtn = this.root.querySelector('[data-select-size]');

    this.chart = null;
    this.recommendedSize = null;

    this.init();
  }

  init() {
    if (this.modal && window.RaidageDialog) {
      this.dialog = new window.RaidageDialog(this.modal, {
        trigger: this.trigger,
        initialFocus: this.modal.querySelector('button[data-modal-close]'),
        show: modal => { modal.style.display = 'block'; },
        hide: modal => { modal.style.display = 'none'; },
        onClose: () => {
          if (this.result) this.result.hidden = true;
          if (this.pickerInput) this.pickerInput.value = '';
          this.highlightColumn(null);
        },
      });
    }
    if (this.selectSizeBtn) {
      this.selectSizeBtn.addEventListener('click', () => this.applySize(), { signal: this.signal });
    }
    if (this.pickerForm) {
      this.pickerForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.runPicker();
      }, { signal: this.signal });
    }

    this.chart = this.parseChart();
    this.render();
  }

  /** Parse the injected JSON; return a usable chart object or null. Never throws. */
  parseChart() {
    if (!this.dataEl) return null;
    const raw = (this.dataEl.textContent || '').trim();
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (!Array.isArray(data.sizes) || data.sizes.length === 0) return null;
      if (!Array.isArray(data.measurements)) data.measurements = [];
      return data;
    } catch (err) {
      console.warn('Size chart JSON is invalid:', err);
      return null;
    }
  }

  render() {
    if (!this.chart) {
      // No metafield / malformed JSON: hide picker, show the static fallback.
      if (this.pickerForm) this.pickerForm.hidden = true;
      if (this.result) this.result.hidden = true;
      if (this.tableWrap) this.tableWrap.innerHTML = '';
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }
    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.titleEl && this.chart.title) this.titleEl.textContent = this.chart.title;

    this.renderTable();
    this.setupPicker();
  }

  /** Build the chart table: sizes are columns, measurement rows render in order. */
  renderTable() {
    if (!this.tableWrap) return;
    const sizes = this.chart.sizes;
    const measurements = this.chart.measurements || [];
    const unit = this.chart.unit || 'cm';

    const table = document.createElement('table');
    table.className = 'size-guide-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const corner = document.createElement('th');
    corner.scope = 'col';
    corner.textContent = `Measurement (${unit})`;
    headRow.appendChild(corner);
    sizes.forEach(size => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = size;
      th.setAttribute('data-col-size', size);
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    measurements.forEach(m => {
      if (!m || typeof m !== 'object') return;
      const tr = document.createElement('tr');
      if (m.code) tr.setAttribute('data-row-code', m.code);

      const rowHead = document.createElement('th');
      rowHead.scope = 'row';
      const label = m.label || m.code || '';
      rowHead.textContent = (m.code && m.label) ? `${m.code} · ${label}` : label;
      tr.appendChild(rowHead);

      const values = (m.values && typeof m.values === 'object') ? m.values : {};
      sizes.forEach(size => {
        const td = document.createElement('td');
        const v = values[size];
        td.textContent = (v === undefined || v === null || v === '') ? '—' : v;
        td.setAttribute('data-col-size', size);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);

    this.tableWrap.innerHTML = '';
    this.tableWrap.appendChild(table);
  }

  /** Resolve the picker row from picker.pick_point, falling back to any pick:true row. */
  getPickPoint() {
    const chart = this.chart;
    if (!chart || !chart.picker) return null;
    const measurements = chart.measurements || [];
    const code = chart.picker.pick_point;
    let row = null;
    if (code) row = measurements.find(m => m && m.code === code);
    if (!row) row = measurements.find(m => m && m.pick === true);
    return row || null;
  }

  setupPicker() {
    if (!this.pickerForm) return;
    const picker = this.chart.picker;
    const pickRow = this.getPickPoint();
    const enabled = !!(picker && picker.enabled && pickRow);

    if (!enabled) {
      this.pickerForm.hidden = true;
      if (this.result) this.result.hidden = true;
      if (this.subtitleEl) this.subtitleEl.textContent = 'Flat garment measurements';
      return;
    }

    const unit = this.chart.unit || 'cm';
    this.pickerForm.hidden = false;
    if (this.pickerLabel) this.pickerLabel.textContent = picker.label || 'Your measurement';
    if (this.pickerUnit) this.pickerUnit.textContent = unit;
    if (this.pickerInput) {
      this.pickerInput.value = '';
      this.pickerInput.placeholder = unit;
    }
    if (this.subtitleEl) this.subtitleEl.textContent = 'Enter a measurement to find your size';
  }

  /**
   * Single-measurement match: smallest size whose (possibly doubled) garment
   * value is >= the shopper's input. Exceeds all -> largest (may run small);
   * below all -> smallest. No ease modelling, no unit conversion.
   */
  runPicker() {
    const row = this.getPickPoint();
    if (!row || !this.pickerInput) return;

    const input = parseFloat(this.pickerInput.value);
    if (!isFinite(input) || input <= 0) return;

    const isHalf = row.match === 'half';
    const values = (row.values && typeof row.values === 'object') ? row.values : {};
    const list = this.chart.sizes
      .map(size => {
        let v = values[size];
        if (typeof v === 'string') v = parseFloat(v);
        if (!isFinite(v)) return null;
        return { size, value: isHalf ? v * 2 : v };
      })
      .filter(Boolean);

    if (!list.length) return;

    let chosen;
    let note = '';
    const fitting = list.filter(x => x.value >= input);
    if (fitting.length) {
      // Smallest garment value that still covers the body measurement (round up to fit).
      chosen = fitting.reduce((a, b) => (a.value <= b.value ? a : b));
    } else {
      // Bigger than everything we make.
      chosen = list.reduce((a, b) => (a.value >= b.value ? a : b));
      note = 'This is our largest size — it may run small for you.';
    }

    this.recommendedSize = chosen.size;
    this.highlightColumn(chosen.size);
    this.showResult(chosen.size, note);
  }

  highlightColumn(size) {
    if (!this.tableWrap) return;
    this.tableWrap.querySelectorAll('[data-col-size]').forEach(el => {
      el.classList.toggle('is-recommended', el.getAttribute('data-col-size') === size);
    });
  }

  showResult(size, note) {
    if (this.recommendedSizeEl) this.recommendedSizeEl.textContent = size;
    if (this.resultNoteEl) this.resultNoteEl.textContent = note || '';
    if (this.result) this.result.hidden = false;
  }

  open() { this.dialog?.open(this.trigger); }
  close() { this.dialog?.close(); }

  /** Reuse the product size buttons: click the one matching the recommendation. */
  applySize() {
    if (!this.recommendedSize) return;
    const target = String(this.recommendedSize).trim().toUpperCase();
    const sizeButtons = this.root.querySelectorAll('[data-size]');
    const sizeBtn = Array.from(sizeButtons).find(
      btn => String(btn.dataset.size || '').trim().toUpperCase() === target
    );
    if (sizeBtn && !sizeBtn.disabled) {
      sizeBtn.click();
    }
    this.close();
  }
}

class ProductForm {
  constructor(root, signal, selection) {
    this.root = root;
    this.selection = selection;
    this.form = root.querySelector('[data-product-form]');
    this.errorMessage = root.querySelector('[data-error-message]');
    this.spinner = root.querySelector('[data-loading-spinner]');
    this.button = root.querySelector('[data-add-to-cart]');
    this.text = root.querySelector('[data-button-text]');
    this.form?.addEventListener('submit', e => this.handleSubmit(e), { signal });
    signal.addEventListener('abort', () => clearTimeout(this.successTimer), { once: true });
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (this.selection.pending) return;
    if (!this.selection.canSubmit()) { this.showError('Please select an available option before adding to cart.'); return; }
    clearTimeout(this.successTimer);
    this.showError('');
    this.selection.pending = true;
    this.selection.sync(this.selection.selectedVariant, false);
    this.form.setAttribute('aria-busy', 'true');
    if (this.spinner) this.spinner.style.display = 'block';
    if (this.text) this.text.style.display = 'none';
    try {
      if (!window.RaidageCart) throw new Error('Cart is unavailable. Please reload and try again.');
      const result = await window.RaidageCart.add(this.selection.selectedVariant.id, this.button);
      if (!this.form.isConnected) return;
      if (result.warning) this.showError(result.warning);
      const status = this.root.querySelector('[data-product-status]');
      if (status) status.textContent = 'Added to cart';
      if (this.text) this.text.textContent = 'ADDED ✓';
      this.successTimer = setTimeout(() => this.selection.updateButton(), 2000);
    } catch (error) {
      if (this.form.isConnected) this.showError(error.message || 'Unable to confirm this add. Check your cart before trying again.');
    } finally {
      this.selection.pending = false;
      this.form.removeAttribute('aria-busy');
      if (this.spinner) this.spinner.style.display = 'none';
      if (this.text) this.text.style.display = 'block';
      const successText = this.text?.textContent;
      this.selection.sync(this.selection.selectedVariant, false);
      if (successText === 'ADDED ✓' && this.text) this.text.textContent = successText;
    }
  }

  showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message;
    this.errorMessage.style.display = message ? 'block' : 'none';
  }
}

class ProductVideo {
  constructor(root, signal) {
    this.player = root.querySelector('[data-video-player]');
    this.toggle = root.querySelector('[data-video-toggle]');
    if (!this.player || !this.toggle) return;
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = false;
    this.visible = false;
    this.toggle.addEventListener('click', () => {
      if (this.player.paused) { this.userPaused = false; this.play(true); }
      else { this.userPaused = true; this.player.pause(); }
    }, { signal });
    this.player.addEventListener('play', () => this.update(), { signal });
    this.player.addEventListener('pause', () => this.update(), { signal });
    this.motion.addEventListener('change', () => { if (this.motion.matches) this.player.pause(); }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.player.pause();
      else if (this.visible && !this.userPaused && !this.motion.matches) this.play();
    }, { signal });
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible && !this.userPaused && !this.motion.matches && !document.hidden) this.play();
        else if (!this.visible) this.player.pause();
      }, { threshold: 0.1 });
      this.observer.observe(this.player);
    }
    signal.addEventListener('abort', () => { this.observer?.disconnect(); this.player.pause(); }, { once: true });
    this.update();
  }

  async play(manual = false) {
    if (!this.loaded) {
      this.player.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
      this.player.load();
      this.loaded = true;
    }
    try {
      await this.player.play();
      if (!manual && (!this.visible || this.userPaused || this.motion.matches || document.hidden)) this.player.pause();
    } catch (_) { this.update(); }
  }

  update() {
    const playing = !this.player.paused;
    this.toggle.textContent = playing ? 'Pause' : 'Play';
    this.toggle.setAttribute('aria-label', playing ? 'Pause product video' : 'Play product video');
    this.toggle.setAttribute('aria-pressed', String(playing));
  }
}

const instances = new WeakMap();
function initProductPage(scope = document) {
  scope.querySelectorAll('[data-product-page]').forEach(page => {
    if (instances.has(page)) return;
    const root = page.closest('.shopify-section') || page.parentElement;
    const controller = new AbortController();
    const gallery = new ProductGallery(root, controller.signal);
    const selection = new ProductSizeSelector(root, controller.signal, gallery);
    new ProductInfoTabs(root, controller.signal);
    const sizeGuide = new SizeCalculator(root, controller.signal);
    new ProductForm(root, controller.signal, selection);
    new ProductVideo(root, controller.signal);
    instances.set(page, { controller, gallery, sizeGuide, selection });
  });
}
window.RaidageProductPage = { init: initProductPage };
document.addEventListener('shopify:section:load', event => initProductPage(event.target));
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-product-page]').forEach(page => {
    const instance = instances.get(page);
    if (!instance) return;
    instance.controller.abort();
    clearTimeout(instance.gallery._scrollTimer);
    instance.sizeGuide.dialog?.destroy();
    instances.delete(page);
  });
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initProductPage(), { once: true });
else initProductPage();

})();
